import type { EdgeState, GridSnapshot, NodeState, PacketState } from "../protocol/snapshot";
import { purityOf } from "./composition";
import type { CardDef } from "./definitions";
import {
  CATALYST_MIN_PURITY,
  OVERHEAT_TICKS_TO_EXPLOSION,
  PRESSURE_BURST_THRESHOLD,
  SLUDGE_RESIDENCE_TICKS,
  VERMIN_RISK_THRESHOLD,
} from "./definitions";
import { heldPenalties } from "./hazardCards";
import { isProcessingNode } from "./hazards";
import { tallyOf } from "./tallies";
import {
  holdTicksOf,
  phaseThresholdOf,
  pressureToleranceOf,
  verminLostTickPeriodOf,
  type EngineEdge,
  type EngineNode,
  type EnginePacket,
  type EngineState,
} from "./state";

/**
 * Projects mutable engine state into the immutable GridSnapshot the renderer
 * draws from. This is the only place the two representations meet, which is
 * what keeps the engine free to mutate in place during a tick.
 */

function projectPacketInNode(packet: EnginePacket, nodeId: string): PacketState {
  return {
    id: packet.id,
    composition: { ...packet.composition },
    purity: purityOf(packet.composition),
    temperature: packet.temperature,
    residenceTicks: packet.residenceTicks,
    isContaminated: packet.isContaminated,
    location: { kind: "inNode", nodeId },
  };
}

function projectPacketOnEdge(packet: EnginePacket, edge: EngineEdge, pathIndex: number): PacketState {
  // t is 0..1 progress along the pipe, which is what the renderer animates.
  const lastIndex = Math.max(edge.path.length - 1, 1);
  return {
    id: packet.id,
    composition: { ...packet.composition },
    purity: purityOf(packet.composition),
    temperature: packet.temperature,
    residenceTicks: packet.residenceTicks,
    isContaminated: packet.isContaminated,
    location: { kind: "onEdge", edgeId: edge.id, t: pathIndex / lastIndex },
  };
}

function projectNode(state: EngineState, node: EngineNode): NodeState {
  const residenceTicks = node.buffer.reduce((max, p) => Math.max(max, p.residenceTicks), 0);
  return {
    id: node.id,
    kind: node.kind,
    pos: { ...node.pos },
    rotation: node.rotation,
    buffer: node.buffer.map((p) => projectPacketInNode(p, node.id)),
    bufferCapacity: node.bufferCapacity,
    feed: node.emits ? { ...node.emits } : null,
    sourcing: node.sourcingOptions
      ? { tier: node.precursor?.tier ?? null, options: node.sourcingOptions }
      : null,
    supplyRemaining: node.supplyRemaining,
    attachments: node.attachments.map((a) => ({ cardId: a.cardId, effect: a.effect })),
    guardCharges: node.guardCharges,
    heatCounter: node.heatCounter,
    criticalTemp: node.criticalTemp,
    isOverheatWarning: node.isOverheatWarning,
    overheatTicks: node.overheatTicks,
    catalystHealth: node.catalystHealth,
    residenceTicks,
    // Q9 (d): `residenceTicks` keeps counting for every non-hopper kind
    // (§3.3's vermin infestation depends on that), but only a processing
    // kind's dwell ever converts — so the residenceTicks fallback below is
    // scoped the same way, or an exempt kind would flash `isSludged` once it
    // parked long enough despite never actually sludging.
    isSludged:
      node.buffer.some((p) => p.hasResidenceSludged) ||
      (isProcessingNode(node) && residenceTicks >= SLUDGE_RESIDENCE_TICKS),
    maxMultipleThisTurn: node.maxMultipleThisTurn,
    holdTicks: holdTicksOf(state, node),
    phaseThreshold: phaseThresholdOf(state, node),
    isDestroyed: node.isDestroyed,
    infestation: node.infestation
      ? { sinceTurn: node.infestation.sinceTurn, lostTickPeriod: verminLostTickPeriodOf(state) }
      : null,
    valveState: node.valveState,
    recipeIds: node.recipeIds,
  };
}

/**
 * `toNode` is passed in rather than looked up here so the burst limit and the
 * `03-hazards.md` §2.2 failure check (`resolveFailures` in hazards.ts) always read the exact
 * same tolerance — one derivation, not two that could drift apart.
 */
function projectEdge(edge: EngineEdge, toNode: EngineNode | undefined): EdgeState {
  return {
    id: edge.id,
    fromNodeId: edge.fromNodeId,
    toNodeId: edge.toNodeId,
    path: edge.path.map((p) => ({ ...p })),
    material: edge.material,
    hp: edge.hp,
    maxHp: edge.maxHp,
    pressureCounter: edge.pressureCounter,
    burstLimit: PRESSURE_BURST_THRESHOLD + (toNode ? pressureToleranceOf(toNode) : 0),
    isOverpressured: edge.isOverpressured,
    isLeaking: edge.isLeaking,
    isBurst: edge.isBurst,
    phaseRole: edge.phaseRole,
    separationRole: edge.separationRole,
  };
}

export function projectSnapshot(state: EngineState, cards: Record<string, CardDef>): GridSnapshot {
  const packetsInFlight: PacketState[] = [];
  for (const edge of state.edges) {
    for (const transit of edge.transit) {
      packetsInFlight.push(projectPacketOnEdge(transit.packet, edge, transit.pathIndex));
    }
  }

  const held = heldPenalties(state, cards);

  return {
    stageId: state.stage.id,
    stageName: state.stage.name,
    cols: state.stage.cols,
    rows: state.stage.rows,
    turn: state.turn,
    phase: state.phase,
    tickInTurn: state.tickInTurn,
    ticksPerTurn: state.stage.ticksPerTurn,
    maxTurns: state.stage.maxTurns,
    energyBudgetKw: state.energyBudgetKw,
    energySpentKw: state.energySpentKw,
    setupBudgetCredits: state.setupBudgetCredits,
    setupSpentCredits: state.setupSpentCredits,
    setupCatalogue: state.stage.setupCatalogue ?? [],
    facilityMitigations: state.installedFacilityMitigations.map((m) => ({ cardId: m.cardId, spent: m.spent })),
    liveBuild: state.stage.liveBuild ? { ...state.stage.liveBuild } : null,
    spillFreeMovesLeft:
      state.phase === "play" && state.stage.liveBuild
        ? Math.max(0, state.stage.liveBuild.spillFreeMovesPerTurn - state.spillFreeMovesUsedThisTurn)
        : 0,
    nodes: state.nodes.map((node) => projectNode(state, node)),
    edges: state.edges.map((edge) =>
      projectEdge(edge, state.nodes.find((n) => n.id === edge.toNodeId)),
    ),
    packetsInFlight,
    objective: {
      targetComposition: { ...state.stage.objective.targetComposition },
      collected: { ...state.collected },
      minPurity: state.stage.objective.minPurity,
    },
    tallies: tallyOf(state),
    hand: state.hand.map((ci) => ({ ...ci })),
    drawPileCount: state.drawPile.length,
    discardPileCount: state.discardPile.length,
    deckCards: [...state.drawPile, ...state.hand, ...state.discardPile].map((ci) => ({ ...ci })),
    deckPenalties: {
      powerPenaltyKw: held.powerPenaltyKw,
      directiveSurchargeKw: held.directiveSurchargeKw,
      handSizePenaltyNextTurn: state.handSizePenaltyNextTurn,
      lockedTiles: held.lockedTiles.map((p) => ({ ...p })),
      lockedHandInstanceIds: held.lockedHandInstanceIds,
    },
    recipes: state.stage.recipes,
    corrosiveSpecies: state.stage.corrosiveSpecies,
    limits: {
      overheatGraceTicks: OVERHEAT_TICKS_TO_EXPLOSION,
      sludgeResidenceTicks: SLUDGE_RESIDENCE_TICKS,
      catalystMinPurity: CATALYST_MIN_PURITY,
      verminRiskThreshold: VERMIN_RISK_THRESHOLD,
    },
    contaminatedTiles: [...state.contaminatedTiles]
      .sort((a, b) => a.pos.y - b.pos.y || a.pos.x - b.pos.x)
      .map((t) => ({ pos: { ...t.pos }, cause: t.cause, sourceId: t.sourceId, sinceTurn: t.sinceTurn })),
    verminRisk: state.verminRisk,
    upcomingScriptedEvents: (state.stage.scriptedEvents ?? []).filter(
      (def) => !state.firedScriptedEvents.includes(`${def.turn}:${def.kind}`),
    ),
    powerFreezeTicks: state.powerFreezeTicks,
    isHeatwaveActive: state.isHeatwaveActive,
    isRunComplete: state.isRunComplete,
    failureReason: state.failureReason,
    isRunFailed: state.isRunFailed,
    forecast: state.forecast.map((f) => ({ ...f })),
  };
}
